import { Injectable, inject } from '@angular/core';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';

import { MyProPage } from 'src/pages/my-pros/my-pro/my-pro';
import { SharedLinkAddPaymentMethodPage } from 'src/pages/shared-link/add-payment-method/add-payment-method';

import { SharedLinkService } from 'src/shared/services/shared-link.service';

import { Util } from 'src/shared/util/util';

import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { ACTION_LINK_PATTERNS } from 'src/shared/constants/action-link-patterns';

interface LinkPattern {
  test: (url: string) => boolean;
  handle: (url: string) => Promise<void>;
}

@Injectable({
  providedIn: 'root'
})
export class ChatLinkRedirectService {

  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly sharedLinkService = inject(SharedLinkService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);

  private readonly handlers: Record<string, (url: string) => Promise<void>> = {
    'my-pro': (url) => this.handleMyProLink(url),
    'shared-link': (url) => this.handleSharedLink(url),
  };

  // Routes recognized URLs in-app instead of opening a new tab
  private readonly patterns: LinkPattern[] = ACTION_LINK_PATTERNS
    .filter(p => this.handlers[p.key])
    .map(p => ({
      test: (url: string) => p.pattern.test(url),
      handle: this.handlers[p.key],
    }));

  async handleLink(url: string): Promise<{ handled: boolean }> {
    for (const pattern of this.patterns) {
      if (pattern.test(url)) {
        localStorage.setItem('autoOpenExpandableChat', 'true');
        await pattern.handle(url);
        return { handled: true };
      }
    }
    this.util.openUrl(url);
    return { handled: false };
  }

  private async handleMyProLink(url: string): Promise<void> {
    const match = url.match(/\/my-pro\/(\d+)/);
    if (!match) {
      return;
    }
    const proId = parseInt(match[1], 10);
    localStorage.setItem('myProBackPage', 'contacts');
    const params = { proId };
    this.rightSidePanelService.navigateTo('my-pro', params, MyProPage);
  }

  private async openInPanel(component: any, params: Record<string, any>): Promise<void> {
    await this.storage.save('dialog-params', params);
    await this.storage.save('dialog-right-side-open', true);
    await this.rightSidePanelService.openRightSidePanel(component);
  }

  private async handleSharedLink(url: string): Promise<void> {
    const match = url.match(/\/shared-link\/([a-f0-9-]+)/);
    if (!match) {return;}
    const uuid = match[1];

    let sharedLink;
    try {
      sharedLink = await this.sharedLinkService.getSharedLink(uuid);
    } catch (err: any) {
      if (err?.status === 404) {
        this.util.showError('This link is no longer available.');
      } else {
        this.util.showError('Something went wrong. Please try again.');
      }
      return;
    }

    if (sharedLink.shared_link_data?.expired) {
      this.util.showError('This link has expired.');
      return;
    }

    const actionType = sharedLink.shared_link_data?.action_type;

    switch (actionType) {
      case 'ai_task_add_payment_method':
        await this.openInPanel(SharedLinkAddPaymentMethodPage, { uuid, sharedLink });
        break;
      default:
        this.util.openUrl(url);
    }
  }

}
